import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { useParams } from "react-router-dom";
import { Line } from "react-chartjs-2";
import {
  Button,
  Card,
  Col,
  Alert,
  Spinner,
  Dropdown,
  DropdownToggle,
  DropdownMenu,
  DropdownItem,
} from "reactstrap";
import Datetime from "react-datetime";
import "react-datetime/css/react-datetime.css";
import moment from "moment";
import "moment/locale/fr";
import Chart from "chart.js";
import zoomPlugin from "chartjs-plugin-zoom";
import { API_URL, getApiConfig } from "../Config/apiConfig";

moment.locale("fr");
// Enregistrement du plugin zoom pour Chart.js v2 (compatibilité avec chartjs-plugin-zoom@0.7.7)
Chart.plugins.register(zoomPlugin);

/* Le backend renvoie déjà les données converties :
   chaque point est de la forme { x: timestamp, y: valeur } */

/* Composant de graphique combinant plusieurs capteurs */
const ChartJsLine = ({ sensorsData, chartOptions }) => {
  // Pour chaque capteur, on crée un dataset.
  // On utilise une palette de couleurs prédéfinie.
  const colors = ["rgba(75,192,192,1)", "rgba(192,75,192,1)"];
  const data = {
    // On suppose que tous les capteurs ont la même échelle temporelle pour l'axe x.
    // Sinon, Chart.js ajustera l'affichage.
    labels:
      sensorsData.length && sensorsData[0].data.length
        ? sensorsData[0].data.map((point) => point.x)
        : [],
    datasets: sensorsData.map((sensor, i) => ({
      label: sensor.label,
      data: sensor.data.map((point) => point.y),
      borderColor: colors[i % colors.length],
      borderWidth: 2, // Ligne doublée
      pointRadius: 0,
      lineTension: 0.2,
      fill: false,
    })),
  };

  // Calcul des limites temporelles issues de l'ensemble des capteurs sélectionnés
  const allDates = sensorsData.flatMap(sensor => sensor.data.map(point => point.x));
  const minDate = allDates.length ? Math.min(...allDates) : null;
  const maxDate = allDates.length ? Math.max(...allDates) : null;
  
  // Mise à jour des limites de zoom pour l'axe X (temps)
  const updatedOptions = {
    ...chartOptions,
    plugins: {
      ...chartOptions.plugins,
      zoom: {
        ...chartOptions.plugins.zoom,
        pan: {
          ...chartOptions.plugins.zoom.pan,
          rangeMin: { x: minDate, y: null },
          rangeMax: { x: maxDate, y: null },
        },
        zoom: {
          ...chartOptions.plugins.zoom.zoom,
          rangeMin: { x: minDate, y: null },
          rangeMax: { x: maxDate, y: null },
        },
      },
    },
  };

  return (
    <div style={{ height: 500 }}>
      <Line data={data} options={updatedOptions} />
    </div>
  );
};

export default function TabChart() {
  const params = useParams();
  const [chartData, setChartData] = useState([]);
  const [config, setConfig] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Période sélectionnée via DateTime, applicable aux deux capteurs
  const [startDate, setStartDate] = useState(moment().subtract(1, "month").toDate());
  const [endDate, setEndDate] = useState(new Date());
  
  // Sélection du premier capteur
  const [selectedSensor, setSelectedSensor] = useState(null);
  // Sélection optionnelle d'un second capteur
  const [selectedSensor2, setSelectedSensor2] = useState(null);

  // Dropdown du premier capteur
  const [sensorDropdownOpen, setSensorDropdownOpen] = useState(false);
  const toggleSensorDropdown = () => setSensorDropdownOpen(prev => !prev);
  // Dropdown pour le second capteur
  const [sensorDropdown2Open, setSensorDropdown2Open] = useState(false);
  const toggleSensorDropdown2 = () => setSensorDropdown2Open(prev => !prev);

  // Transformation des données : pour chaque sensor, on crée un objet { id, label, data }
  const formatChartData = useCallback((allChartData, configData) => {
    return configData.map(sensor => {
      const sensorData = allChartData.map(entry => ({
        x: new Date(entry.epoch_date * 1000).getTime(),
        y: entry[`capteur_${sensor.id}_data`],
      }));
      return {
        id: sensor.id,
        label: sensor.label || `Capteur ${sensor.id}`,
        data: sensorData,
      };
    });
  }, []);

  // Options pour Chart.js v2 avec zoom/pan et axe temps formaté
  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    animation: false,
    parsing: false,
    tooltips: {
      mode: "index",
      intersect: false,
      callbacks: {
        label: function (tooltipItem, data) {
          return data.datasets[tooltipItem.datasetIndex].label + ": " + tooltipItem.yLabel + " °C";
        },
      },
    },
    hover: {
      mode: "nearest",
      intersect: true,
    },
    scales: {
      xAxes: [
        {
          type: "time",
          time: {
            tooltipFormat: "DD MMM YYYY HH[H]mm",
            displayFormats: {
              hour: "DD MMM YYYY HH[h]",
              day: "DD MMM YYYY",
              minute: "DD MMM YYYY HH:mm",
            },
          },
          scaleLabel: {
            display: true,
            labelString: "Temps",
          },
        },
      ],
      yAxes: [
        {
          scaleLabel: {
            display: true,
            labelString: "°C",
          },
          ticks: {
            beginAtZero: true,
          },
        },
      ],
    },
    plugins: {
      zoom: {
        pan: {
          enabled: true,
          mode: "x",
          rangeMin: { x: null, y: null },
          rangeMax: { x: null, y: null },
        },
        zoom: {
          enabled: true,
          drag: false,
          mode: "x",
          speed: 0.1,
          rangeMin: { x: null, y: null },
          rangeMax: { x: null, y: null },
        },
      },
    },
  };

  useEffect(() => {
    setLoading(true);
    setError(null);

    const queryParams = [];
    // Construit le paramètre capteur_id en fonction des capteurs sélectionnés
    if (selectedSensor && selectedSensor2) {
      queryParams.push(`capteur_id=${selectedSensor},${selectedSensor2}`);
    } else if (selectedSensor) {
      queryParams.push(`capteur_id=${selectedSensor}`);
    }
    if (startDate) queryParams.push(`start_date=${startDate.getTime()}`);
    if (endDate) queryParams.push(`end_date=${endDate.getTime()}`);
    const queryString = queryParams.length > 0 ? `?${queryParams.join("&")}` : "";

    const getAllChartDataRequest = axios.get(
      `${API_URL}getAllChartData/${params.id}${queryString}`,
      getApiConfig()
    );
    const getConfigRequest = axios.get(
      `${API_URL}getConfig/${params.id}`,
      getApiConfig()
    );

    axios.all([getAllChartDataRequest, getConfigRequest])
      .then(axios.spread((allChartDataResponse, configResponse) => {
        const allChartData = allChartDataResponse.data;
        const rawConfig = configResponse.data || [];

        // Garder uniquement les capteurs actifs (state === 1)
        const activeConfig = rawConfig.filter(s => Number(s.state) === 1); 
        setConfig(activeConfig);

        // Sélection automatique du premier capteur actif s'il n'est pas défini
        if (!selectedSensor && activeConfig.length > 0) {
          setSelectedSensor(activeConfig[0].id);
        }

        // Si le second capteur sélectionné n'est plus actif, on le réinitialise
        if (selectedSensor2 && !activeConfig.find(s => s.id === selectedSensor2)) {
          setSelectedSensor2(null);
        }

        const formattedData = formatChartData(allChartData, activeConfig);
        setChartData(formattedData);
        setLoading(false);
      }))
      .catch(err => {
        console.error("Erreur lors du chargement des données:", err);
        setError("Impossible de charger les données. Veuillez réessayer.");
        setLoading(false);
      });
  }, [params.id, formatChartData, selectedSensor, selectedSensor2, startDate, endDate]);

  // Construit la liste des données à afficher en combinant les capteurs sélectionnés
  const combinedSensorsData = chartData.filter(sensorData => {
    return sensorData.id === selectedSensor || sensorData.id === selectedSensor2;
  });

  return (
    <Col>
      {error && <Alert color="danger">{error}</Alert>}

      {/* Sélection des capteurs */}
      <div className="row mb-4 align-items-center">
        <div className="col-md-4 mb-2 mb-md-0">
          <label className="me-2">Premier capteur :</label>
          <Dropdown
            isOpen={sensorDropdownOpen}
            toggle={toggleSensorDropdown}
            disabled={config.length === 0}
          >
            <DropdownToggle caret color="info" outline>
              {config.find(s => s.id === selectedSensor)?.label || "Sélectionner un capteur"}
            </DropdownToggle>
            <DropdownMenu>
              {config.map(sensor => (
                <DropdownItem
                  key={sensor.id}
                  active={selectedSensor === sensor.id}
                  onClick={() => setSelectedSensor(sensor.id)}
                >
                  {sensor.label || `Capteur ${sensor.id}`}
                </DropdownItem>
              ))}
            </DropdownMenu>
          </Dropdown>
        </div>
        <div className="col-md-4">
          <label className="me-2">Second capteur (optionnel) :</label>
          <Dropdown
            isOpen={sensorDropdown2Open}
            toggle={toggleSensorDropdown2}
            disabled={config.length === 0}
          >
            <DropdownToggle caret color="secondary" outline>
              {config.find(s => s.id === selectedSensor2)?.label || "Ajouter un capteur"}
            </DropdownToggle>
            <DropdownMenu>
              {config.map(sensor => (
                <DropdownItem
                  key={sensor.id}
                  active={selectedSensor2 === sensor.id}
                  onClick={() =>
                    // Si le capteur sélectionné comme premier est choisi, on le retire du second
                    setSelectedSensor2(sensor.id === selectedSensor ? null : sensor.id)
                  }
                >
                  {sensor.label || `Capteur ${sensor.id}`}
                </DropdownItem>
              ))}
            </DropdownMenu>
          </Dropdown>
        </div>
      </div>

      {/* Sélecteurs de dates */}
      <div className="row mb-4 align-items-center">
        <div className="col-md-6">
          <label className="me-2">Date de début :</label>
          <Datetime
            value={startDate}
            timeFormat={true}
            dateFormat="DD/MM/YYYY"
            onChange={date => {
              const newStart = date.toDate ? date.toDate() : new Date(date);
              if (newStart > endDate) {
                setEndDate(newStart);
              }
              setStartDate(newStart);
            }}
          />
        </div>
        <div className="col-md-6">
          <label className="me-2">Date de fin :</label>
          <Datetime
            value={endDate}
            timeFormat={true}
            dateFormat="DD/MM/YYYY"
            onChange={date => {
              const newEnd = date.toDate ? date.toDate() : new Date(date);
              if (newEnd < startDate) {
                alert("La date de fin doit être postérieure ou égale à la date de début.");
              } else {
                setEndDate(newEnd);
              }
            }}
          />
        </div>
      </div>

      {loading ? (
        <div className="text-center py-5">
          <Spinner color="primary" />
          <p className="mt-2">Chargement des données...</p>
        </div>
      ) : chartData.length === 0 ? (
        <Alert color="info">Aucune donnée disponible pour la période sélectionnée.</Alert>
      ) : (
        <Card className="chart-container p-3">
          <h3 className="chart-label text-center mb-3">
            {combinedSensorsData.map(s => s.label).join(" / ")}
          </h3>
          {combinedSensorsData.length === 0 || combinedSensorsData[0].data.length === 0 ? (
            <Alert color="warning">
              Aucune donnée disponible pour les capteurs sélectionnés dans la période indiquée.
            </Alert>
          ) : (
            <ChartJsLine sensorsData={combinedSensorsData} chartOptions={chartOptions} />
          )}
        </Card>
      )}
    </Col>
  );
}
